import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ConfirmationModalService } from '@epicuro-next/ataraxia/modal';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import {
  clearBasketValidations,
  initExpandDefaultBasketLines,
  loadBasketSuccess,
  loadOpenedBasket,
  resetExpandedLines,
  resetOpenedBasket,
} from '../state/actions';
import {
  selectActiveBasketId,
  selectActiveBasketIdResolved,
  selectDtoBasketEntities,
  selectIsMakeBasketActiveEnabled,
} from '../state/selectors';

const LEAVE_BASKET_TITLE = $localize`:cbasket|Leave basket confirm modal title@@confirmLeaveBasketModalTitle:Confirm`;
const LEAVE_BASKET_MESSAGE = $localize`:cbasket|Leave basket confirm modal text@@confirmLeaveBasketModalText:Are you sure that you want to leave current basket and continue with your active basket?`;

@Injectable()
export class OpenedBasketGuard {
  constructor(
    private store: Store,
    private router: Router,
    private confirmationModalService: ConfirmationModalService,
  ) {}

  private selectIsMakeBasketActiveEnabled$ = this.store.pipe(
    select(selectIsMakeBasketActiveEnabled),
  );

  public canActivate(
    routeSnapshot: ActivatedRouteSnapshot,
  ): Observable<boolean> {
    return this.waitActiveBasketResolved().pipe(
      concatLatestFrom(() => [
        this.store.pipe(select(selectActiveBasketId)),
        this.store.pipe(select(selectDtoBasketEntities)),
      ]),
      switchMap(([_, activeBasketId, entities]) => {
        const basketId = routeSnapshot.params.basketId as string;

        if (basketId) {
          this.store.dispatch(
            initExpandDefaultBasketLines({
              basketId,
            }),
          );
        }

        const submittedBasket =
          this.router.getCurrentNavigation()?.extras?.state?.basket;

        const dtoFromEntity = entities[basketId];

        if (basketId !== activeBasketId) {
          if (submittedBasket) {
            /*
             * To be removed altogether with grouping on GQL
             */
            this.store.dispatch(
              loadBasketSuccess({ basketData: submittedBasket }),
            );
          } else if (!dtoFromEntity) {
            this.store.dispatch(loadOpenedBasket({ basketId }));
          }
        }

        return of(true);
      }),
    );
  }

  public canDeactivate() {
    // switching basket in basket manager or creating basket from accounts menu
    const forceLeaveBasket =
      this.router.getCurrentNavigation()?.extras?.state?.forceLeaveBasket;

    return this.selectIsMakeBasketActiveEnabled$.pipe(
      take(1),
      switchMap((isDifferent) =>
        isDifferent && !forceLeaveBasket
          ? this.confirmationModalService.open({
              message: LEAVE_BASKET_MESSAGE,
              title: LEAVE_BASKET_TITLE,
              hideFooter: false,
              showCloseBtn: false,
              options: { attachBrowserHistory: false },
            })
          : of(true),
      ),
      tap(() => {
        this.store.dispatch(resetOpenedBasket());
        this.store.dispatch(clearBasketValidations());
        this.store.dispatch(resetExpandedLines());
      }),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  private waitActiveBasketResolved() {
    return this.store.pipe(
      select(selectActiveBasketIdResolved),
      filter(Boolean),
      take(1),
    );
  }
}
